import { createClient } from '@supabase/supabase-js'

import { supabase as sharedSupabase } from '@flexwise/shared/lib/supabaseClient'
import { getKioskSupabaseClient } from '../app/kiosk/utils/kioskUtils'

export const supabase = sharedSupabase

// Login is handled directly in features/auth/components/LoginPage.tsx via
// supabase.auth.signInWithPassword; AuthContext + MFAGuard drive the
// subsequent MFA flow. The legacy `handleLogin` / `progressiveLoginWithMFA`
// path was deleted as part of the MFA dead-code sweep (2026-04-19).

// Logout function
export async function handleLogout() {
  try {
    void 0 && console.log('🚪 Logging out...')

    // Clear RTK Query cache to prevent cross-school data leakage
    try {
      const { resetAllApiCaches } = await import('../store')
      resetAllApiCaches()
    } catch (cacheError) {
      console.warn('⚠️ Could not clear RTK Query cache during logout:', cacheError)
    }

    // Legacy persistent-challenge clear removed along with
    // mfa-challenge-storage — the current flow does not persist challenges
    // across reloads. See MFAGuard + authApi.verifyTotp.

    const { error } = await supabase.auth.signOut({ scope: 'local' })

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('fw_demo_session')
    }

    if (error) {
      console.error('❌ Logout error:', error)
      void 0 && console.log('🔄 Local session cleared despite error')
    } else {
      void 0 && console.log('✅ Logout successful')
    }

    // No hard reload. AuthContext listens to SIGNED_OUT and router will render login.
    return { ok: !error }

  } catch (err) {
    console.error('💥 Logout failed:', err)
    // Return failure; caller can still rely on auth listener to swap UI.
    return { ok: false, error: err }
  }
}

// Cached current user profile to avoid repeated role lookups across features
let cachedCurrentUserProfile = null
let cachedCurrentUserProfileUserId = null
let cachedCurrentUserProfilePromise = null

// Get current user profile using the correct connection pattern
export async function getCurrentUserProfile() {
  try {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      void 0 && console.log('❌ No authenticated user found')
      cachedCurrentUserProfile = null
      cachedCurrentUserProfileUserId = null
      cachedCurrentUserProfilePromise = null
      return null
    }

    if (cachedCurrentUserProfile && cachedCurrentUserProfileUserId === user.id) {
      return cachedCurrentUserProfile
    }

    if (cachedCurrentUserProfilePromise && cachedCurrentUserProfileUserId === user.id) {
      return await cachedCurrentUserProfilePromise
    }

    cachedCurrentUserProfileUserId = user.id
    cachedCurrentUserProfilePromise = (async () => {
      void 0 && console.log('👤 Getting profile for user:', {
        auth_id: user.id,
        email: user.email,
        profile_id: user.user_metadata?.profile_id
      })

      const profileId = user.user_metadata?.profile_id
      let profile = null

      if (profileId) {
        void 0 && console.log('🔎 Looking up profile using profile_id:', profileId)
        void 0 && console.log('📋 Full user metadata:', user.user_metadata)

        const { data: profileData, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', profileId)
          .single()

        void 0 && console.log('📊 Database query result by profile_id:', { profile: profileData, error })

        if (error) {
          try {
            console.error('❌ Profile lookup by profile_id failed:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2))
          } catch (e) {
            console.error('❌ Profile lookup by profile_id failed:', error)
          }
        } else {
          profile = profileData
        }
      }

      if (!profile) {
        try {
          void 0 && console.log('🔎 Attempting fallback profile lookup by auth user_id or email')
          const userId = user.id
          const userEmail = user.email

          const { data: fallbackProfiles, error: fallbackError } = await supabase
            .from('user_profiles')
            .select('*')
            .or(`user_id.eq.${userId},email.eq.${userEmail}`)
            .limit(1)

          if (fallbackError) {
            try {
              console.error('❌ Fallback profile lookup error:', JSON.stringify(fallbackError, Object.getOwnPropertyNames(fallbackError), 2))
            } catch (e) {
              console.error('❌ Fallback profile lookup error:', fallbackError)
            }
          }

          if (fallbackProfiles && fallbackProfiles.length > 0) {
            profile = fallbackProfiles[0]
            void 0 && console.warn('⚠️ Fallback profile lookup succeeded. Consider fixing user_metadata.profile_id for a safer lookup.')
          } else {
            void 0 && console.warn('⚠️ No profile found via fallback lookup (user_id/email).')
          }
        } catch (e) {
          console.error('💥 Exception during fallback profile lookup:', e)
        }
      }

      if (!profile) {
        console.error('��� No profile found for user after all lookup attempts:', { email: user.email })
        cachedCurrentUserProfile = null
        cachedCurrentUserProfilePromise = null
        return null
      }

      let role = 'Parent'
      try {
        const { data: userRoleRows, error: urErr } = await supabase
          .from('user_roles')
          .select('role_id')
          .eq('user_profile_id', profileId)

        if (urErr) throw urErr

        const roleIds = (userRoleRows || []).map(r => r.role_id).filter(Boolean)
        if (roleIds.length > 0) {
          const { data: roleDefs, error: rErr } = await supabase
            .from('roles')
            .select('id, name')
            .in('id', roleIds)
          if (rErr) throw rErr

          const names = (roleDefs || []).map(r => r.name).filter(Boolean)
          const precedence = ['Admin', 'Schulleitung', 'Sekretariat', 'Teacher', 'Erzieher*innen', 'Student', 'Parent']
          role = names.sort((a, b) => precedence.indexOf(a) - precedence.indexOf(b))[0] || 'Parent'
        }
      } catch (roleErr) {
        void 0 && console.warn('⚠️ Role lookup fallback used due to error:', roleErr)
      }

      void 0 && console.log('✅ Profile loaded successfully:', {
        profile_id: profile.id,
        name: `${profile.first_name} ${profile.last_name}`,
        role: role,
        school_id: profile.school_id
      })

      const result = {
        ...profile,
        role: role
      }

      cachedCurrentUserProfile = result
      cachedCurrentUserProfilePromise = null
      return result
    })()

    return await cachedCurrentUserProfilePromise
  } catch (error) {
    console.error('💥 Error in getCurrentUserProfile:', error)
    cachedCurrentUserProfile = null
    cachedCurrentUserProfileUserId = null
    cachedCurrentUserProfilePromise = null
    return null
  }
}

// Role-based routing logic
export function getRouteByRole(role) {
  const routes = {
    'Parent': '/dashboard/parent',
    'Teacher': '/dashboard/teacher',
    'Admin': '/dashboard/admin',
    'Student': '/dashboard/student',
    'Erzieher*innen': '/dashboard/teacher', // Use teacher dashboard for erzieher
    'Externe': '/dashboard/external',
    'Super Admin': '/dashboard/admin'
  }

  void 0 && console.log('🗺️ Getting route for role:', role, '→', routes[role] || '/dashboard/parent')
  return routes[role] || '/dashboard/parent'
}

// RLS Context Setup
export async function setupRLSContext() {
  const { data: { session } } = await supabase.auth.getSession()
  return session
}

// Route Protection
export async function checkAccess() {
  const session = await supabase.auth.getSession()
  return session.data.session
}

// Utility to select large datasets in batches with consistent paging
export async function selectInBatches({
  table,
  columns = '*',
  apply,
  pageSize = 1000,
  maxPages = 100,
  signal
} = {}) {
  if (!table) throw new Error('table is required')
  const results = []
  for (let page = 0; page < maxPages; page++) {
    if (signal?.aborted) break
    let query = supabase.from(table).select(columns)
    if (typeof apply === 'function') query = apply(query)
    const from = page * pageSize
    const to = from + pageSize - 1
    const { data, error } = await query.range(from, to)
    if (error) throw error
    const chunk = Array.isArray(data) ? data : []
    results.push(...chunk)
    if (chunk.length < pageSize) break
  }
  return results
}

// Fetch lessons from vw_react_lesson_details view
export async function fetchLessonsForTeacher(teacherId) {
  try {
    void 0 && console.log('📚 Fetching lessons from vw_react_lesson_details for teacher:', teacherId)

    // Query for lessons where user is primary teacher
    const { data: primaryLessons, error: primaryError } = await supabase
      .from('vw_react_lesson_details')
      .select('*')
      .eq('primary_teacher_id', teacherId)

    if (primaryError) {
      console.error('❌ Error fetching primary teacher lessons:', primaryError)
      throw primaryError
    }

    // Query for lessons where user is in teacher_ids array (supporting teacher)
    const { data: supportingLessons, error: supportingError } = await supabase
      .from('vw_react_lesson_details')
      .select('*')
      .contains('teacher_ids', [teacherId])

    if (supportingError) {
      console.error('❌ Error fetching supporting teacher lessons:', supportingError)
      throw supportingError
    }

    // Combine and deduplicate results
    const allLessons = [...primaryLessons, ...supportingLessons]
    const uniqueLessons = allLessons.filter((lesson, index, self) =>
      index === self.findIndex(l => l.lesson_id === lesson.lesson_id)
    )

    // Sort by start time
    const lessons = uniqueLessons.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))

    if (error) {
      console.error('�� Error fetching lessons:', error)
      throw error
    }

    void 0 && console.log('✅ Lessons fetched successfully:', lessons?.length || 0, 'lessons')
    return lessons || []

  } catch (error) {
    console.error('💥 Error in fetchLessonsForTeacher:', error)
    throw error
  }
}

// Fetch lessons for current date (base timetable view + substitution enrichment)
export async function fetchTodaysLessons(teacherId, date = new Date()) {
  try {
    const startOfDay = new Date(date)
    startOfDay.setHours(0, 0, 0, 0)
    const endOfDay = new Date(date)
    endOfDay.setHours(23, 59, 59, 999)

    void 0 && console.log('📅 Fetching lessons for date:', date.toISOString().split('T')[0])

    // Query for lessons where user is primary teacher
    const { data: primaryLessons, error: primaryError } = await supabase
      .from('vw_react_lesson_details')
      .select('*')
      .eq('primary_teacher_id', teacherId)
      .gte('start_datetime', startOfDay.toISOString())
      .lt('start_datetime', endOfDay.toISOString())

    if (primaryError) {
      console.error('❌ Error fetching primary teacher lessons:', primaryError)
      throw primaryError
    }

    // Query for lessons where user is in teacher_ids array (supporting teacher)
    const { data: supportingLessons, error: supportingError } = await supabase
      .from('vw_react_lesson_details')
      .select('*')
      .contains('teacher_ids', [teacherId])
      .gte('start_datetime', startOfDay.toISOString())
      .lt('start_datetime', endOfDay.toISOString())

    if (supportingError) {
      console.error('❌ Error fetching supporting teacher lessons:', supportingError)
      throw supportingError
    }

    // Query for lessons where user is primary substitute teacher
    const { data: substitutePrimaryLessons, error: substitutePrimaryError } = await supabase
      .from('vw_react_lesson_details')
      .select('*')
      .eq('substitute_primary_teacher_id', teacherId)
      .gte('start_datetime', startOfDay.toISOString())
      .lt('start_datetime', endOfDay.toISOString())

    if (substitutePrimaryError) {
      console.error('❌ Error fetching substitute primary teacher lessons:', substitutePrimaryError)
      throw substitutePrimaryError
    }

    // Query for lessons where user is in substitute_begleitung_teacher_ids array (accompanying substitute)
    const { data: substituteBegleitungLessons, error: substituteBegleitungError } = await supabase
      .from('vw_react_lesson_details')
      .select('*')
      .contains('substitute_begleitung_teacher_ids', [teacherId])
      .gte('start_datetime', startOfDay.toISOString())
      .lt('start_datetime', endOfDay.toISOString())

    if (substituteBegleitungError) {
      console.error('❌ Error fetching substitute begleitung teacher lessons:', substituteBegleitungError)
      throw substituteBegleitungError
    }

    // Combine and deduplicate results
    const allLessons = [...primaryLessons, ...supportingLessons, ...substitutePrimaryLessons, ...substituteBegleitungLessons]
    const uniqueLessons = allLessons.filter((lesson, index, self) =>
      index === self.findIndex(l => l.lesson_id === lesson.lesson_id)
    )

    // Enrich with substitution view for original/absent kürzels (limited to today + these lessons)
    const lessonIds = uniqueLessons.map(l => l.lesson_id)
    let subsByLesson = {}
    if (lessonIds.length > 0) {
      try {
        const { data: subRows, error: subError } = await supabase
          .from('vw_react_lessons_needing_substitute')
          .select('lesson_id, original_teacher_kurzel, absent_teacher_kurzels')
          .in('lesson_id', lessonIds)
        if (!subError && Array.isArray(subRows)) {
          subRows.forEach((row) => {
            if (row && row.lesson_id) subsByLesson[row.lesson_id] = row
          })
        } else if (subError) {
          console.error('❌ Error fetching substitution view for timetable:', subError)
        }
      } catch (e) {
        console.error('❌ Exception enriching timetable with substitutions:', e)
      }
    }

    const enriched = uniqueLessons.map((lesson) => {
      const extra = subsByLesson[lesson.lesson_id] || {}
      return {
        ...lesson,
        original_teacher_kurzel: extra.original_teacher_kurzel || lesson.original_teacher_kurzel || null,
        absent_teacher_kurzels: Array.isArray(extra.absent_teacher_kurzels)
          ? extra.absent_teacher_kurzels
          : (lesson.absent_teacher_kurzels || null)
      }
    })

    // Sort by start time
    const lessons = enriched.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))

    void 0 && console.log('��� Today\'s lessons fetched:\', lessons?.length || 0, \'lessons')
    return lessons || []

  } catch (error) {
    console.error('💥 Error in fetchTodaysLessons:', error)
    throw error
  }
}

// Fetch attendance badge data for lessons
export async function fetchAttendanceBadges(lessonIds) {
  try {
    void 0 && console.log('🏷️ Fetching attendance badges for lessons:', lessonIds)

    // Handle empty or null lesson IDs
    if (!lessonIds || lessonIds.length === 0) {
      void 0 && console.log('📋 No lesson IDs provided, returning empty badges')
      return {}
    }

    const { data: badges, error } = await supabase
      .from('vw_lesson_attendance_badges')
      .select('*')
      .in('lesson_id', lessonIds)

    if (error) {
      console.error('Error fetching attendance badges:', error?.message || error, error)
      throw error
    }

    // Convert to lookup object for easy access
    const badgeLookup = {}
    badges?.forEach(badge => {
      badgeLookup[badge.lesson_id] = badge
    })

    void 0 && console.log('✅ Attendance badges fetched:', Object.keys(badgeLookup).length, 'badges')
    return badgeLookup

  } catch (error) {
    console.error('💥 Error in fetchAttendanceBadges:', error)
    return {} // Return empty object on error to prevent crashes
  }
}

// Attendance tracking functions
export async function fetchLessonAttendance(lessonId) {
  try {
    void 0 && console.log('��� Fetching attendance for lesson:', lessonId)
    void 0 && console.log('📋 Lesson ID type:', typeof lessonId)

    // First, let's try a simple query to see if the table exists
    void 0 && console.log('🔍 Testing table access...')
    const { data: testData, error: testError } = await supabase
      .from('student_attendance_logs')
      .select('*')
      .limit(1)

    if (testError) {
      console.error('❌ Table access failed:', testError)
      console.error('❌ Error message:', testError.message || 'No message')
      console.error('❌ Error code:', testError.code || 'No code')
      console.error('❌ Error details:', testError.details || 'No details')
      console.error('❌ Error hint:', testError.hint || 'No hint')
      throw new Error(`Table access failed: ${testError.message}`)
    }

    void 0 && console.log('✅ Table access successful, test data:', testData)

    // Query without foreign key hint to avoid PostgREST schema cache issues
    const { data: attendance, error } = await supabase
      .from('student_attendance_logs')
      .select(`
        *,
        user_profiles:student_id(first_name, last_name)
      `)
      .eq('lesson_id', lessonId)

    if (error) {
      console.error('❌ Error fetching attendance:', error)
      console.error('❌ Error message:', error.message || 'No message')
      console.error('❌ Error code:', error.code || 'No code')
      console.error('❌ Error details:', error.details || 'No details')
      console.error('❌ Error hint:', error.hint || 'No hint')
      throw new Error(`Attendance fetch failed: ${error.message}`)
    }

    void 0 && console.log('📋 Raw attendance data:', attendance)

    // Group attendance by status
    const grouped = {
      present: attendance?.filter(record => record.status === 'present') || [],
      late: attendance?.filter(record => record.status === 'late') || [],
      absent: attendance?.filter(record =>
        record.status === 'absent_excused' || record.status === 'absent_unexcused'
      ) || []
    }

    void 0 && console.log('✅ Attendance fetched and grouped:', grouped)
    return grouped

  } catch (error) {
    console.error('💥 Error in fetchLessonAttendance:', error)
    console.error('💥 Error name:', error.name || 'No name')
    console.error('💥 Error message:', error.message || 'No message')
    console.error('💥 Error stack:', error.stack || 'No stack')
    throw error
  }
}

export async function saveAttendanceRecord(lessonId, studentId, status, additionalData = {}) {
  try {
    void 0 && console.log('💾 Saving attendance record:', { lessonId, studentId, status, additionalData })

    const attendanceRecord = {
      lesson_id: lessonId,
      student_id: studentId,
      status: status, // 'present', 'late', 'absent'
      recorded_at: new Date().toISOString(),
      ...additionalData // Can include notes, late_minutes, etc.
    }

    const { data, error } = await supabase
      .from('student_attendance_logs')
      .upsert(attendanceRecord, {
        onConflict: 'lesson_id,student_id',
        ignoreDuplicates: false
      })
      .select()

    if (error) {
      console.error('❌ Error saving attendance:', error)
      throw error
    }

    void 0 && console.log('✅ Attendance record saved:', data)
    return data

  } catch (error) {
    console.error('💥 Error in saveAttendanceRecord:', error)
    throw error
  }
}

export async function bulkSaveAttendance(lessonId, attendanceRecords) {
  try {
    void 0 && console.log('📚 Bulk saving attendance for lesson:', lessonId, attendanceRecords)

    const records = attendanceRecords.map(record => ({
      lesson_id: lessonId,
      student_id: record.studentId,
      status: record.status,
      recorded_at: new Date().toISOString(),
      notes: record.notes || null,
      late_minutes: record.lateMinutes || null
    }))

    const { data, error } = await supabase
      .from('student_attendance_logs')
      .upsert(records, {
        onConflict: 'lesson_id,student_id',
        ignoreDuplicates: false
      })
      .select()

    if (error) {
      console.error('❌ Error bulk saving attendance:', error)
      throw error
    }

    void 0 && console.log('✅ Bulk attendance saved:', data)
    return data

  } catch (error) {
    console.error('💥 Error in bulkSaveAttendance:', error)
    throw error
  }
}

export const isDemo = false // Always use real authentication now

// Fetch lesson meta from vw_react_lesson_details (compatible with substitute lessons)
export async function getLessonMeta(lessonId) {
  const { data, error } = await supabase
    .from('vw_react_lesson_details')
    .select('lesson_id, school_id, course_id, class_id, start_datetime, end_datetime')
    .eq('lesson_id', lessonId)
    .single()
  if (error) throw error

  // Map lesson_id to id for backward compatibility
  if (data) {
    return {
      id: data.lesson_id,
      school_id: data.school_id,
      course_id: data.course_id,
      class_id: data.class_id,
      start_datetime: data.start_datetime,
      end_datetime: data.end_datetime
    }
  }
  return data
}


// Build student ID ↔ name pairs for a lesson
export async function getLessonStudentNameIdPairs(lessonId, schoolId = null) {
  try {
    const lesson = await getLessonMeta(lessonId)
    if (!lesson) {
      return { students: [], schoolId: schoolId }
    }

    // Helper to fetch profiles and classes
    const fetchProfilesAndClasses = async (studentIds, classIdOverride = null) => {
      if (!studentIds.length) return []

      let query = supabase.from('user_profiles').select('id, first_name, last_name').in('id', studentIds)
      if (schoolId) query = query.eq('school_id', schoolId)
      const { data: profiles, error: pErr } = await query
      if (pErr) throw pErr

      let classMap = {}
      if (classIdOverride) {
        profiles.forEach(p => { classMap[p.id] = classIdOverride })
      } else {
        // Use mv_student_current_class instead of legacy profile_info_student
        let mscQuery = supabase.from('vw_student_current_class') /* matview removed from API per gold-std §11a */.select('student_id, class_id').in('student_id', studentIds)
        if (schoolId) mscQuery = mscQuery.eq('school_id', schoolId)
        const { data: msc, error: mscErr } = await mscQuery
        if (mscErr) throw mscErr
        classMap = (msc || []).reduce((acc, r) => { acc[r.student_id] = r.class_id; return acc }, {})
      }

      const classIds = Array.from(new Set(Object.values(classMap).filter(Boolean)))
      let classNameById = {}
      if (classIds.length) {
        // Lookup by id — keep structure_classes so archived/historical classes still resolve to a name.
        let classQuery = supabase.from('structure_classes').select('id, name').in('id', classIds)
        if (schoolId) classQuery = classQuery.eq('school_id', schoolId)
        const { data: classes, error: cErr } = await classQuery
        if (cErr) throw cErr
        classNameById = (classes || []).reduce((acc, c) => { acc[c.id] = c.name; return acc }, {})
      }

      return profiles.map(p => {
        const className = classNameById[classMap[p.id]] || ''
        const displayName = `${p.first_name || ''} ${p.last_name || ''}${className ? ` (${className})` : ''}`.trim()
        return { id: p.id, displayName }
      })
    }

    // 1) Course-based: try enrollments first
    if (lesson.course_id) {
      // Extract lesson date for filtering time-bounded enrollments (e.g., Gastkinder)
      const lessonDate = lesson.start_datetime
        ? new Date(lesson.start_datetime).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10)

      let enrollmentQuery = supabase
        .from('course_enrollments')
        .select('student_id')
        .eq('course_id', lesson.course_id)
        .lte('start_date', lessonDate)
        .gte('end_date', lessonDate)
      if (schoolId) enrollmentQuery = enrollmentQuery.eq('school_id', schoolId)
      const { data: enrollments, error: eErr } = await enrollmentQuery
      if (eErr) throw eErr
      const studentIds = (enrollments || []).map(e => e.student_id).filter(Boolean)
      const students = await fetchProfilesAndClasses(studentIds)
      return { students, schoolId: lesson.school_id }
    }


    // 2) Class-based roster (use mv_student_current_class instead of legacy profile_info_student)
    if (lesson.class_id) {
      let mscQuery = supabase.from('vw_student_current_class') /* matview removed from API per gold-std §11a */.select('student_id').eq('class_id', lesson.class_id)
      if (schoolId) mscQuery = mscQuery.eq('school_id', schoolId)
      const { data: msc, error: mscErr } = await mscQuery
      if (mscErr) throw mscErr
      const studentIds = (msc || []).map(r => r.student_id).filter(Boolean)
      const students = await fetchProfilesAndClasses(studentIds, lesson.class_id)
      return { students, schoolId: lesson.school_id }
    }

    return { students: [], schoolId: lesson.school_id }
  } catch (error) {
    console.error('Error in getLessonStudentNameIdPairs:', error);
    throw error;
  }
}

// Fetch single lesson by ID for attendance modal fallback
export async function fetchSingleLesson(lessonId) {
  try {

    const { data: lesson, error } = await supabase
      .from('vw_react_lesson_details')
      .select('*')
      .eq('lesson_id', lessonId)
      .single();

    if (error) {
      console.error('❌ Error fetching lesson:', error);
      return null;
    }

    if (!lesson) {
      void 0 && console.warn('⚠️ No lesson found with ID:', lessonId);
      return null;
    }

    // Fetch student data for the lesson
    let students = [];
    try {
      const result = await getLessonStudentNameIdPairs(lessonId);
      const { students: studentData } = result;
      students = studentData.map(s => ({
        id: s.id,
        name: s.displayName,
        className: s.className || ''
      }));
    } catch (error) {
      void 0 && console.warn('Could not fetch students for lesson:', lessonId, error);
      // Try alternative student fetch if available
      if (lesson.class_id) {
        try {
          // matview removed from API per gold-std §11a — INVOKER view replacement
          const { data: classMembership, error: classError } = await supabase
            .from('vw_student_current_class')
            .select('student_id')
            .eq('class_id', lesson.class_id);

          if (!classError && classMembership && classMembership.length) {
            const memberIds = classMembership.map(m => m.student_id).filter(Boolean);
            const { data: profiles } = await supabase
              .from('user_profiles')
              .select('id, first_name, last_name')
              .in('id', memberIds);
            if (profiles) {
              students = profiles.map(s => ({
                id: s.id,
                name: `${s.first_name || ''} ${s.last_name || ''}`.trim()
              }));
            }
          }
        } catch (classError) {
          void 0 && console.warn('⚠️ Could not fetch class students either:', classError);
        }
      }
    }

    // Transform to teacher dashboard lesson format
    const startTime = new Date(lesson.start_datetime);
    const endTime = new Date(lesson.end_datetime);

    // Prefer course name for modules (course-based lessons), hide unknown class label in header
    const subjectTitle = lesson.course_id ? (lesson.course_name || lesson.subject_name || 'Flexmodul') : (lesson.subject_name || 'Unbekannt');
    const classLabel = lesson.course_id ? '' : (lesson.class_name || '');

    return {
      id: lesson.lesson_id,
      school_id: lesson.school_id,
      class_id: lesson.class_id || null,
      course_id: lesson.course_id || null,
      is_course: !!lesson.course_id,
      time: new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Berlin' }).format(startTime),
      endTime: new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Berlin' }).format(endTime),
      start_datetime: lesson.start_datetime,
      end_datetime: lesson.end_datetime,
      subject: subjectTitle,
      class: classLabel,
      room: lesson.room_name,
      location: lesson.room_name,
      isCurrent: false, // Will be calculated if needed
      isSubstitute: lesson.lesson_type === 'substitute',
      isCancelled: lesson.is_cancelled || false,
      otherTeachers: [],
      adminComment: lesson.notes,
      students: students,
      enrolled: students.length || lesson.student_count || 0,
      attendance: { present: [], late: [], absent: [] },
      attendanceTaken: lesson.attendance_taken || false
    };

  } catch (error) {
    console.error('💥 Error in fetchSingleLesson:', error);
    return null;
  }
}

// Fetch active school classes for dropdown
export async function fetchSchoolInformation() {
  try {
    void 0 && console.log('🏫 Fetching school information from database')

    const { data: schoolInfo, error } = await supabase
      .from('vw_react_school_information')
      .select('*')
      .maybeSingle()

    if (error) {
      const msg = normalizeSupabaseError(error)
      console.error('��� Error fetching school information:', msg, error)
      throw new Error(msg)
    }

    if (!schoolInfo) {
      void 0 && console.warn('⚠️ No school information found for current school')
      return null
    }

    void 0 && console.log('✅ School information fetched successfully:', {
      name: schoolInfo.name,
      email: schoolInfo.email,
      hasLeadership: !!schoolInfo.leadership
    })

    return schoolInfo

  } catch (error) {
    const msg = normalizeSupabaseError(error)
    console.error('💥 Error in fetchSchoolInformation:', msg)
    throw new Error(msg)
  }
}

export async function fetchSchoolClasses(schoolId) {
  try {
    if (!schoolId) return []
    const { data: classes, error } = await supabase
      .from('vw_active_classes')
      .select('id, name, grade_level, year')
      .eq('school_id', schoolId)
      .order('grade_level', { ascending: true })
      .order('name', { ascending: true })
    if (error) throw error
    return classes || []
  } catch (error) {
    console.error('Error fetching school classes:', error)
    return []
  }
}

export async function fetchMyClassTeacherClassIds(staffId, schoolId) {
  try {
    if (!staffId || !schoolId) {
      return { mainClassIds: [], linkedClassIds: [] }
    }

    const { data, error } = await supabase
      .from('staff_class_links')
      .select('class_id,is_class_teacher,role')
      .eq('staff_id', staffId)
      .eq('school_id', schoolId)

    if (error) throw error

    const rows = Array.isArray(data) ? data.filter(row => !!row?.class_id) : []
    const linkedClassIds = new Set(rows.map(row => row.class_id))
    const mainRoleValues = new Set(['klassenleitung', 'class_lead', 'main'])
    const mainClassIds = new Set(
      rows
        .filter(row => {
          if (row?.is_class_teacher) return true
          const role = typeof row?.role === 'string' ? row.role.toLowerCase() : ''
          return mainRoleValues.has(role)
        })
        .map(row => row.class_id)
    )

    return {
      mainClassIds: Array.from(mainClassIds),
      linkedClassIds: Array.from(linkedClassIds)
    }
  } catch (error) {
    console.error('Error fetching class teacher class ids:', error)
    return { mainClassIds: [], linkedClassIds: [] }
  }
}

export async function fetchLessonDiaryEntry(lessonId) {
  try {
    void 0 && console.log('📝 Fetching lesson diary entry for lesson:', lessonId)

    const { data: diaryEntries, error } = await supabase
      .from('lesson_diary_entries')
      .select('*')
      .eq('lesson_id', lessonId)
      .order('created_at', { ascending: false })
      .limit(1)

    if (error) {
      console.error('❌ Error fetching lesson diary entry:', error)
      throw error
    }

    const latestEntry = diaryEntries?.[0]
    void 0 && console.log('✅ Lesson diary entry fetched:', latestEntry?.entry_text || 'No entry found')

    return latestEntry?.entry_text || ''

  } catch (error) {
    console.error('💥 Error in fetchLessonDiaryEntry:', error)
    return ''
  }
}

export async function saveLessonAttendanceBulkRPC({ lessonId, schoolId, attendance, diaryText, diaryPrivate = false, recordedBy: passedRecordedBy = null }) {
  // Caller may pass recordedBy directly (e.g. when they already have the
  // user profile in scope) to skip an auth.getUser() round-trip per call.
  let recordedBy = passedRecordedBy
  if (!recordedBy) {
    const profile = await getCurrentUserProfile()
    recordedBy = profile?.id || null
  }
  if (!recordedBy) {
    throw new Error('Kein gültiges Benutzerprofil gefunden (recorded_by). Bitte melden Sie sich neu an oder wenden Sie sich an den Administrator.')
  }

  const payload = {
    p_lesson_id: lessonId,
    p_school_id: schoolId,
    p_attendance: attendance,
    p_recorded_by: recordedBy,
    p_diary_entry_text: diaryText || null,
    p_diary_is_private: !!diaryPrivate
  }
  // Pick the right Supabase client for this call. On a paired kiosk the
  // shared singleton `supabase` is bootstrapped with the anon JWT — and
  // anon doesn't have EXECUTE on save_lesson_attendance_bulk (kiosk does).
  // The RTK Query `supabaseBaseQuery` solves this via `pickClient()` for
  // every call it routes, but this RPC is called directly outside RTK
  // Query (useAttendanceSave, TagesgastCard, useTripAttendanceSave,
  // kiosk klassenbuch mutations, teacherDashboard) — none of those go
  // through baseQuery. Without this picker, a paired kiosk would always
  // get 42501 "permission denied for function save_lesson_attendance_bulk".
  // Mirrors supabaseBaseQuery.ts:pickClient() precedence: authenticated
  // user session wins, then kiosk JWT, then anon bootstrap.
  const client = pickClientForRpc()
  const { data, error } = await client.rpc('save_lesson_attendance_bulk', payload)
  if (error) throw error
  return data
}

// Picker shared by every non-RTK-Query RPC site in this module. Keep in
// sync with supabaseBaseQuery.ts:pickClient — same rules, same precedence.
function pickClientForRpc() {
  try {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return supabase
    // 1. Authenticated user session present → shared client (covers
    //    dev/admin browser tab where a teacher is logged in normally,
    //    even on a machine that ALSO happens to have a kiosk JWT cached).
    const host = (typeof location !== 'undefined') ? location.hostname.split('.')[0] : ''
    if (host && localStorage.getItem(`sb-${host}-auth-token`)) return supabase
    // 2. No user session — fall through to the kiosk client when a
    //    kiosk JWT is available.
    const kioskJwt = localStorage.getItem('fw_kiosk_jwt')
    if (!kioskJwt) return supabase
    // getKioskSupabaseClient is imported at module top; it memoises
    // its client by JWT so repeated calls are cheap.
    return getKioskSupabaseClient() ?? supabase
  } catch {
    return supabase
  }
}

function normalizeSupabaseError(err) {
  if (!err) return 'Unbekannter Fehler';
  if (typeof err === 'string') return err;
  const parts = [err.message, err.details, err.hint, err.code]
    .filter(Boolean)
    .map(s => (typeof s === 'string' ? s : JSON.stringify(s)))
  return parts.join(' | ') || 'Unbekannter Fehler';
}

export async function saveSchoolInformation(schoolData) {
  try {
    void 0 && console.log('��� Saving school information:', schoolData)

    const { data, error } = await supabase.rpc('save_school_information_react', {
      p_name: schoolData.name,
      p_leadership: schoolData.leadership,
      p_address: schoolData.address,
      p_email: schoolData.email,
      p_phone: schoolData.phone,
      p_fax: schoolData.fax,
      p_signature: schoolData.signature,
      p_logo_url: schoolData.logo_url || null
    })

    if (error) {
      const msg = normalizeSupabaseError(error)
      console.error('❌ Error saving school information:', msg, error)
      throw new Error(msg)
    }

    void 0 && console.log('��� School information saved successfully:', data)
    return data?.[0] || { success: true, message: 'Saved successfully' }

  } catch (error) {
    console.error('💥 Error in saveSchoolInformation:', error)
    throw error
  }
}

export async function uploadSchoolLogo(file) {
  try {
    void 0 && console.log('📤 Uploading school logo:', file.name)

    const fileExt = file.name.split('.').pop()
    const fileName = `school-logo-${Date.now()}.${fileExt}`

    // Resolve current user's school_id via secure RPC (respects RLS)
    const { data: schoolData, error: schoolError } = await supabase.rpc('get_user_school_id')
    if (schoolError || !schoolData) {
      console.error('❌ Error getting user school ID via RPC:', schoolError)
      throw new Error('Could not determine user school')
    }

    const schoolId = String(schoolData)
    const filePath = `${schoolId}/logos/${fileName}`

    const { data, error } = await supabase.storage
      .from('school-logo')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        metadata: {
          school_id: schoolId
        }
      })

    if (error) {
      console.error('❌ Error uploading logo:', error)
      throw error
    }

    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from('school-logo')
      .createSignedUrl(filePath, 60 * 60 * 24 * 365)

    if (signedUrlError) {
      console.error('❌ Error creating signed URL:', signedUrlError)
      const { data: { publicUrl } } = supabase.storage
        .from('school-logo')
        .getPublicUrl(filePath)

      void 0 && console.log('���️ Signed URL failed, storing file path for later signed URL generation:', filePath)
      return {
        success: true,
        logo_url: filePath,
        file_path: filePath
      }
    }

    void 0 && console.log('✅ Logo uploaded successfully with signed URL:', signedUrlData.signedUrl)
    return {
      success: true,
      logo_url: filePath,
      file_path: filePath
    }

  } catch (error) {
    console.error('💥 Error in uploadSchoolLogo:', error)
    throw error
  }
}

export async function getSchoolLogoSignedUrl(logoPathOrUrl) {
  try {
    if (!logoPathOrUrl) return null

    void 0 && console.log('������ Getting fresh signed URL for logo:', logoPathOrUrl)

    let filePath = logoPathOrUrl

    if (logoPathOrUrl.startsWith('logos/') || /.+\/logos\//.test(logoPathOrUrl)) {
      filePath = logoPathOrUrl
    } else if (logoPathOrUrl.includes('/object/sign/school-logo/')) {
      const urlParts = logoPathOrUrl.split('/object/sign/school-logo/')
      if (urlParts.length >= 2) {
        filePath = decodeURIComponent(urlParts[1].split('?')[0])
      }
    } else if (logoPathOrUrl.includes('/object/public/school-logo/')) {
      const urlParts = logoPathOrUrl.split('/object/public/school-logo/')
      if (urlParts.length >= 2) {
        filePath = decodeURIComponent(urlParts[1].split('?')[0])
      }
    } else if (logoPathOrUrl.includes('/school-logo/')) {
      const urlParts = logoPathOrUrl.split('/school-logo/')
      if (urlParts.length >= 2) {
        filePath = decodeURIComponent(urlParts[1].split('?')[0])
      }
    }

    filePath = filePath.split('?')[0].split('#')[0]

    if (/^https?:\/\//.test(filePath)) {
      const m = filePath.match(/\/school-logo\/(.+)$/)
      if (m && m[1]) filePath = decodeURIComponent(m[1])
    }

    const candidates = []

    if (/.+\/logos\//.test(filePath)) {
      candidates.push(filePath)
    } else if (filePath.startsWith('logos/')) {
      try {
        const { data, error } = await supabase.rpc('get_user_school_id')
        if (!error && data) {
          const schoolId = String(data)
          candidates.push(`${schoolId}/${filePath}`)
        }
      } catch (e) {
        void 0 && console.warn('⚠️ Could not resolve school_id for logo path scoping via RPC:', e?.message || e)
      }
      candidates.push(filePath)
    } else {
      candidates.push(filePath)
    }

    const tryCreateSigned = async (p) => {
      const { data, error } = await supabase.storage
        .from('school-logo')
        .createSignedUrl(p, 60 * 60)
      return { data, error, path: p }
    }

    for (const candidate of candidates) {
      const { data, error, path } = await tryCreateSigned(candidate)
      if (!error && data?.signedUrl) {
        void 0 && console.log('✅ Fresh signed URL created successfully for path:', path)
        return data.signedUrl
      }
      void 0 && console.warn('⚠️ Signed URL failed for path:', path)
    }

    try {
      const fallbackPath = candidates[0]
      const { data: { publicUrl } } = supabase.storage
        .from('school-logo')
        .getPublicUrl(fallbackPath)
      if (publicUrl) {
        void 0 && console.log('⚠️ Using public URL fallback for logo:', publicUrl)
        return publicUrl
      }
    } catch (e) {
      console.error('❌ Public URL fallback failed:', e)
    }

    console.error('❌ All attempts to create a signed URL failed. Candidates tried:', candidates)
    return null

  } catch (error) {
    console.error('💥 Error in getSchoolLogoSignedUrl:', error)
    return null
  }
}

export async function deleteSchoolLogo(logoPathOrUrl) {
  try {
    if (!logoPathOrUrl) return { success: true }

    void 0 && console.log('🗑️ Deleting school logo:', logoPathOrUrl)

    let filePath = logoPathOrUrl

    if (logoPathOrUrl.startsWith('logos/')) {
      filePath = logoPathOrUrl
    }
    else if (logoPathOrUrl.includes('/school-logo/')) {
      const urlParts = logoPathOrUrl.split('/school-logo/')
      if (urlParts.length >= 2) {
        filePath = decodeURIComponent(urlParts[1])
        filePath = filePath.split('?')[0]
      }
    }

    if (!filePath || !filePath.startsWith('logos/')) {
      void 0 && console.warn('⚠️ Invalid logo path format:', logoPathOrUrl)
      return { success: true }
    }

    void 0 && console.log('��� Deleting file path:', filePath)

    const { error } = await supabase.storage
      .from('school-logo')
      .remove([filePath])

    if (error) {
      console.error('❌ Error deleting logo:', error)
      return { success: false, error: error.message }
    }

    void 0 && console.log('✅ Logo deleted successfully')
    return { success: true }

  } catch (error) {
    console.error('💥 Error in deleteSchoolLogo:', error)
    return { success: false, error: error.message }
  }
}

// 2FA and Device Trust Utilities
export async function userRequires2FA(userProfile) {
  try {
    if (!userProfile) return false
    const adminRoles = ['Admin', 'Super Admin']
    if (userProfile.roles?.name && adminRoles.includes(userProfile.roles.name)) return true

    const { data: userRoles, error } = await supabase
      .from('user_roles')
      .select('*, roles(name)')
      .eq('user_profile_id', userProfile.id)

    if (error) return false
    const hasAdminRole = (userRoles || []).some(ur => ur.roles?.name && adminRoles.includes(ur.roles.name))
    return hasAdminRole
  } catch {
    return false
  }
}

export async function userHas2FAEnabled(user) {
  try {
    const { data: factors, error } = await supabase.auth.mfa.listFactors()
    if (error) return false

    const hasVerified = factors.totp.some(f => f.status === 'verified') ||
      (factors.phone?.some(f => f.status === 'verified')) ||
      !!(user?.phone_confirmed_at && user?.phone)
    return !!hasVerified
  } catch {
    return false
  }
}

export async function logSecurityEvent(eventType, details = {}) {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    const logEntry = {
      event_type: eventType,
      user_id: user?.id || null,
      user_email: user?.email || null,
      details: {
        ...details,
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
      }
    }
    void 0 && console.log('🔒 Security Event:', logEntry)
    return logEntry
  } catch (error) {
    console.error('Error logging security event:', error)
  }
}

export async function checkSecureMFAHealth() {
  const health = {
    timestamp: new Date().toISOString(),
    status: 'healthy',
    issues: [],
    components: {}
  }

  try {
    try {
      const { data: factors, error: mfaError } = await supabase.auth.mfa.listFactors()
      health.components.supabase_mfa = {
        status: mfaError ? 'error' : 'healthy',
        error: mfaError?.message,
        total_factors: factors?.all?.length || 0,
        totp_factors: factors?.totp?.length || 0,
        phone_factors: factors?.phone?.length || 0
      }
      if (mfaError) {
        health.issues.push('Supabase MFA service unavailable')
        health.status = 'degraded'
      }
    } catch (mfaError) {
      health.components.supabase_mfa = { status: 'error', error: mfaError.message }
      health.issues.push('Supabase MFA service error')
      health.status = 'degraded'
    }

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser()
      health.components.auth_service = {
        status: authError ? 'error' : 'healthy',
        error: authError?.message,
        user_authenticated: !!user
      }
      if (authError) {
        health.issues.push('Authentication service error')
        health.status = 'degraded'
      }
    } catch (authError) {
      health.components.auth_service = { status: 'error', error: authError.message }
      health.issues.push('Authentication service failure')
      health.status = 'degraded'
    }

    try {
      const { error: dbError } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .range(0, 0)
      health.components.database = {
        status: dbError ? 'error' : 'healthy',
        error: dbError?.message
      }
      if (dbError) {
        health.issues.push('Database connection failed')
        health.status = 'degraded'
      }
    } catch (dbError) {
      health.components.database = { status: 'error', error: dbError.message }
      health.issues.push('Database connection error')
      health.status = 'degraded'
    }

    void 0 && console.log('🏥 Secure MFA Health Check:', health)
    return health

  } catch (error) {
    console.error('���� Health check failed:', error)
    return {
      ...health,
      status: 'error',
      error: error.message,
      issues: [...health.issues, 'Health check system failure']
    }
  }
}
